import axios from "axios";
import { LRUCache } from "lru-cache";
import { API } from "revolt.js";
import styles from "./Attachment.module.scss";
import { Text } from "preact-i18n";
import { useEffect, useState } from "preact/hooks";

import { Preloader } from "@revoltchat/ui";

import { useClient } from "../../../../controllers/client/ClientController";
import RequiresOnline from "../../../../controllers/client/jsx/RequiresOnline";

interface Props {
    attachment: API.File;
}

const TRUNCATE_THRESHOLD = 1024;
const CACHE_MAX_SIZE = 2 * 1024 * 1024;

const fileCache = new LRUCache<string, string>({
    maxSize: CACHE_MAX_SIZE,
    sizeCalculation: (item) => item.length,
});

export default function TextFile({ attachment }: Props) {
    const [content, setContent] = useState<undefined | string>(undefined);
    const [loading, setLoading] = useState(false);
    const [truncated, setTruncated] = useState(false);

    const client = useClient();
    const apiUrl = client.configuration?.features?.autumn?.url;
    const isLarge = attachment.size > TRUNCATE_THRESHOLD;
    const fileUrl = apiUrl
        ? `${apiUrl}/attachments/${attachment._id}`
        : undefined;
    const previewUrl = apiUrl
        ? `${apiUrl}/attachments/preview/${attachment._id}`
        : undefined;
    const targetUrl = isLarge ? previewUrl : fileUrl;

    useEffect(() => {
        if (typeof content !== "undefined") return;
        if (loading) return;
        if (!targetUrl) return;

        setLoading(true);
        setTruncated(isLarge);

        const cached = fileCache.get(targetUrl);
        if (cached) {
            setContent(cached);
            setLoading(false);
        } else {
            axios
                .get(targetUrl, { transformResponse: [] })
                .then((res) => {
                    setContent(res.data);
                    fileCache.set(targetUrl, res.data);
                    setLoading(false);
                })
                .catch(() => {
                    console.error(
                        "Failed to load text file. [",
                        attachment._id,
                        "]",
                    );
                    setLoading(false);
                });
        }
    }, [content, loading, targetUrl, attachment._id, attachment.size, isLarge]);

    return (
        <div
            className={styles.textContent}
            data-loading={typeof content === "undefined"}>
            {content ? (
                <>
                    <pre>
                        <code>{content}</code>
                    </pre>
                    {truncated && (
                        <div className={styles.truncatedLabel}>
                            <Text id="app.main.channel.misc.truncated" />
                        </div>
                    )}
                </>
            ) : (
                <RequiresOnline>
                    <Preloader type="ring" />
                </RequiresOnline>
            )}
        </div>
    );
}
